import { DateTime } from "luxon";
import { Module } from "vuex";

import { PractitionersAPI } from "@/api/practitioners";
import { SlotsApi } from "@/api/slots";
import { RootState } from "@/types";
import { Availabilities, Availability, AvailabilitySlots, CurrentSlot } from "@/types/Availability";

import { deserializeSlots, getWeekends, serializeSlots, uniqBy } from "./helpers";

interface SlotsState {
  availabilities: Availabilities;
  initialAvailabilities: Availabilities;
  slots?: AvailabilitySlots;
  slotsArray: Availability[];
  weekends: string[];
  currentSlot: CurrentSlot | null;
}

interface ModuleContext {
  state: SlotsState;
  rootState: any;
  rootGetters: any;
  commit: any;
}

export const slots: Module<SlotsState, RootState> = {
  namespaced: true,
  state: {
    availabilities: [],
    initialAvailabilities: [],
    slots: { "1": [], "2": [], "3": [], "4": [], "5": [], "6": [], "7": [] },
    weekends: [],
    slotsArray: [],
    currentSlot: null,
  },
  mutations: {
    setAvailabilities(state: SlotsState, payload: Availabilities) {
      state.availabilities = payload;
    },
    setInitialAvailabilities(state: SlotsState, payload: Availabilities) {
      state.initialAvailabilities = payload;
    },
    setPractitionerSlots(state: SlotsState, payload: AvailabilitySlots) {
      state.slots = payload;
    },
    setWeekends(state: SlotsState, payload: string[]) {
      state.weekends = payload;
    },
    setSlotsArray(state, payload) {
      state.slotsArray = payload;
    },
    setCurrentSlot(state, payload) {
      state.currentSlot = payload;
    },
  },
  actions: {
    async savePractitionerAvailability({ commit, state, rootGetters }: ModuleContext) {
      const { availabilities } = state;
      const { uid, timeZone, locale } = rootGetters;
      const availabilitySlots = deserializeSlots(availabilities);
      await PractitionersAPI.practitionerUpdateSettings(uid, { timeZone, locale, availabilitySlots });
      commit("setInitialAvailabilities", availabilities);
    },
    async getPractitionerAvailability({ commit, rootGetters }: ModuleContext) {
      const { uid } = rootGetters;
      const { availabilitySlots } = await PractitionersAPI.practitionerGetSettings(uid);
      const availabilities = serializeSlots(availabilitySlots);
      const weekends = getWeekends(availabilitySlots);
      commit("setAvailabilities", availabilities);
      commit("setInitialAvailabilities", availabilities);
      commit("setPractitionerSlots", availabilitySlots);
      commit("setWeekends", weekends);
    },
    async getPractitionerFreeSlots({ commit, rootGetters }: ModuleContext, practitionerId?: string) {
      const {
        "patient/patientsPractitioner": { id },
      } = rootGetters;
      const slots = await SlotsApi.getAllFiltered({ practitionerId: practitionerId || id, status: "free" });
      const uniqSlots = uniqBy(slots, "start");
      commit("setSlotsArray", uniqSlots);
    },
    async getSlotById(ctx, slotId) {
      const slot = await SlotsApi.getById(slotId);
      ctx.commit("checkout/setVisitDate", DateTime.fromISO(slot.start).toFormat("yyyy-MM-d"), {
        root: true,
      });
      ctx.commit("setCurrentSlot", slot);
    },
  },
  getters: {
    availabilities(state) {
      return state.availabilities;
    },
    initialAvailabilities(state) {
      return state.initialAvailabilities;
    },
    slots(state) {
      return state.slots;
    },
    weekends(state) {
      return state.weekends;
    },
    slotsArray(state) {
      return state.slotsArray;
    },
    currentSlot(state) {
      return state.currentSlot;
    },
  },
};
